import { HeroBackground, ProductMain } from '@prishapolicy/shared/ui';
import {
  BasicCard,
  ChoosePlan,
  ChoosePlanProps,
  Faq,
  getPurchaseFlowHeroForms,
  HeroTicker,
  liabilityfaqList,
  ProductCategoryPageProps,
  productsPathAdder,
  // protectCardCheck,
  ProtectToday,
  useW3PurchaseFlow,
  WhyChooseUs,
} from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import heroImage from './hero-image.png';
import liability1 from './liability-insurance-1.png';
const productCategorySlug = 'liability-insurance';

export function LiabilityInsurance(props: ProductCategoryPageProps) {
  const typeOfInsurances: ChoosePlanProps['cards'] = [
    {
      title: 'Public Liability Insurance',
      content:
        'MSMEs typically do not try to obtain liability insurance policies because the premiums are highly priced; however, in the event of a claim, the legal costs usually outweigh the tips. As a result, getting insurance is usually more sensible.',
    },
    {
      title: 'Product Liability',
      content:
        'This is again not a compulsory insurance requirement in many countries, but it is highly important. This is procured by companies whose products are widely used such as chemicals, tobacco, medical products, food, recreational products and others.',
    },
    {
      title: 'Employer Liability',
      content:
        'This type offers cover to liabilities that an employer may incur if an employee is injured during his/her employment due to the job. Sometimes, companies do not deem this as important but if faced with a claim, they might be driven to bankruptcy.',
    },
    {
      title: 'Third-Party Liability',
      content:
        'This policy covers damages caused by the insured to another. The insured is considered as the first party, the insurance company is the second and the third is the injured or the person/company making the claims.',
    },
    {
      title: 'Indemnity insurance ',
      content: `Indeminty insurance can provide coverage to protect any business against negligence claims that may arise due to a financial harm resulting from mistakes or failure to perform as per the agreement.
      It is also known as error and omission insurance.`,
    },
  ];
  const choosePlanCards: ChoosePlanProps['cards'] = [
    {
      title: 'Scope of <br/>Cover',
      content: (
        <div>
          It is very important to understand what type of insurance policy you are purchasing and what type of
          liabilities are your protecting yourself from.
          <br />
          <br />A proper audit of your services from a risk management or insurance audit company can help you
          understand your liabilities better.
        </div>
      ),
    },
    {
      title: 'Understanding your clients',
      content: (
        <div>
          As a business, you want to show your customers that you&apos;re responsible and trustworthy. Getting insurance
          shows you understand the risks you face during your daily operations.
          <br />
          <br />
          Thus, selecting the right insurance policy can become quintessential for your business
        </div>
      ),
    },
    {
      title: 'Claim Settlement Services',
      content: (
        <div>
          A liability insurance claim can be very draining on an insurer&apos;s pockets, so the claim settlement
          capabilities of an insurer are very important. In accordance with the provisions of the Protection of
          Policyholders&apos; Interest Regulations, most insurers include an arbitration clause in the policy to protect
          themselves from liability. It is critical to select the right insurer.{' '}
        </div>
      ),
    },
    {
      title: 'Main Exclusions of the Policy',
      content: (
        <div>
          Every liability policy contain certain sets of exclusion, though these exlusions can be understood and some of
          them may get covered after underwriting approvals, there are always some main exclusions that the insurer will
          not protect you against. Keep an eye out for which coverages does your policy exclude.
        </div>
      ),
    },
  ];

  const { IconRenderer, FormRenderer } = useW3PurchaseFlow(props);

  return (
    <>
      <NextSeo
        title="Liability Insurance | PrishaPolicy"
        description="Liability Insurance : Protect your business from financial losses and legal liabilities with our comprehensive liability insurance coverage"
      />
      <ProductMain color="yellow">
        <HeroBackground backgroundColor="yellow-400" PurchaseFlowFormRenderer={FormRenderer}>
          <div className="grid-cols-2 lg:grid ">
            <div className="flex flex-col justify-between">
              <div className="flex flex-col gap-3">
                <h1 className="heading-1 pb-3">
                  Because your
                  <br />
                  paycheck also needs
                  <br />
                  protection.
                </h1>
                <p className="text-title-6 max-w-[600px]">
                  Protect your business from financial losses and legal liabilities with our comprehensive liability
                  insurance coverage
                </p>
              </div>
              <IconRenderer />
            </div>
            <div className="hidden w-full items-end justify-center lg:flex">
              <div className="lg:w-3/4">
                <Image src={heroImage} alt="contact us" />
              </div>
            </div>
          </div>
        </HeroBackground>
        <HeroTicker />
        <ProtectToday productCategoryPrefix={productCategorySlug} maxPerRow={3} items={props.productPaths} />
        <div className="container py-24">
          <div className=" grid-cols-2 gap-x-24 lg:grid ">
            <div className="flex flex-col justify-center">
              <h3 className="lg:heading-3 heading-4">Liability Insurance—what is it? </h3>
              <p className="prose mt-4">
                Liability insurance refers to an insurance product that protects an insured party from claims resulting
                from injuries and property damage to others. This insurance ensures coverage from any legal costs and
                payouts incurred if an insured party is found legally liable.
                <br />
                <br />
                In general, liability insurance policies do not cover intentional damage or contractual liabilities.
              </p>
            </div>
            <div className="grid place-items-center ">
              <Image src={liability1} alt="liability insurance" />
            </div>
            <div className="col-span-2 mt-16">
              <h3 className="heading-3">The Necessity of Liability Insurance</h3>
              <p className="mt-3 max-w-[1100px]">
                Liability insurance is one of the most critical business components for entrepreneurs operating in
                India. With a population of 1.25 billion and a growing inclination towards entrepreneurship, owning a
                small business in India has become an attractive proposition. You or a member of your organization can
                make a mistake that injures someone or damages property. Your mistake could harm the reputation or
                interfere with the privacy of a customer, client, competitor or member of the general public.
              </p>
            </div>
          </div>
          <h3 className="heading-3 my-6">Types of Liability Insurance</h3>
          <div className="mt-8 flex shrink-0 gap-x-3 overflow-x-auto">
            {typeOfInsurances.map((card, index) => {
              return (
                <BasicCard
                  key={index}
                  title={card.title}
                  content={card.content}
                  className="min-w-[85%] lg:min-w-[25%]"
                />
              );
            })}
          </div>
        </div>
        <ChoosePlan cards={choosePlanCards} perRow={4} />
        <WhyChooseUs />
        <Faq list={liabilityfaqList} />
        <div style={{ height: '100px' }}></div>
      </ProductMain>
    </>
  );
}

export function getStaticProps() {
  const productPaths = productsPathAdder(productCategorySlug);
  return {
    props: {
      ...getPurchaseFlowHeroForms(productCategorySlug).props,
      productPaths,
    },
  };
}
export default LiabilityInsurance;
